import { useCallback, useRef, useEffect } from 'react';
import multiItemAccessoryUpdateCartService from '../../../services/MultiItemAccessories/multiitemAccessoriesUpdateCartService';
import { actionTypes } from './MultiAccessoryProvider';
import { CONTRACT, DATA_POSTPAID, HARDWARE, HARDWAREGROUPNAME, INTERNETFLOW, MOBILERATEPLAN, NONE, RATEPLAN, TABLETS, TV, WATCHES, HARDWARE_CONFIGURATION, HARDWARE_IMAGE_URL, HARDWARE_CATEGORY, UPFRONT, SMARTWATCH, TABLET_CATEGORY, UPDATE } from '../../../utils/constants';
import { countZeros } from '../../../utils/utils';
import { ACCESSORY, ADD, FLOWINTITIATED, MONTHLYFLASHDISCOUNT, REMOVE, TOTALFLASHDISCOUNT, MONTHLY } from '../../../utils/constants';
import { updateCreatecartItemData } from './types';
import { findAllCartItemsByCategory } from '../../../utils/DeviceUtils';
import { useMultiAccessoryContext } from './useMultiAccessoryContext';

/**
 * Custom hook that provides action creators for Multi Accessory Context
 * Reads state directly from MultiAccessoryContext — no need to pass dispatch or state from parent
 * @returns Object containing all action creator functions
 */
export const useMultiAccessoryUpdateCartActions = () => {
  const { store, dispatch } = useMultiAccessoryContext();
  const storeRef = useRef(store);

  useEffect(() => {
    storeRef.current = store;
  }, [store]);

  // Action: Set Update Cart Failure
  const setupdateAccessoryCartFailure = useCallback((payload: any) => {
    dispatch({ type: actionTypes.updateAccessoryCartFailure, payload });
  }, [dispatch]);

  /**
   * Parses the update-cart response as the latest authoritative cart state.
   * Only preserves subscribedInfo and processType from existing cartData if missing.
   */
  const mergeCartData = (responseCartItems: any[]): Record<string, any> => {
    const updatedCartData = findAllCartItemsByCategory(responseCartItems);
    const existingCartData = storeRef.current.cartData;
    
    // Preserve only metadata that's not part of the cart structure itself
    if (!updatedCartData.subscribedInfo && existingCartData?.subscribedInfo) {
      updatedCartData.subscribedInfo = existingCartData.subscribedInfo;
    }
    if (!updatedCartData.processType && existingCartData?.processType) {
      updatedCartData.processType = existingCartData.processType;
    }
    return updatedCartData;
  };

  /**
   * @param redirectUrl => redirection url for checkout page
   */

  const multiItemAccessorycreateCartRequest = useCallback(async (
    updateCreatecartItemData: updateCreatecartItemData,
  ) => {
    try {
      const multiAccessoryState = storeRef.current;
      const { selectedPaymentType , recommendedAccessories , selectedTab, deviceSessionValues} = multiAccessoryState;
      //check for WIN/rateplan flow
      const isSubscriptionFlow = [MOBILERATEPLAN, INTERNETFLOW, TV ]?.includes?.(recommendedAccessories?.[selectedTab]?.hardware?.[0]?.recommendationProcessingType) ? true : (deviceSessionValues?.hardwareVariant ? false : true);

      let updateCartRequest: any;

      if (updateCreatecartItemData?.paymentTermOnly) {
        // Payment term change only — no cartItem array.
        // Use newPaymentType if provided (avoids stale storeRef when called right after setSelectedPaymentType).
        const effectivePaymentType = updateCreatecartItemData?.newPaymentType ?? selectedPaymentType;
        updateCartRequest = {
          ...(isSubscriptionFlow && { recommendationItemTerm: effectivePaymentType === CONTRACT ? MONTHLY : NONE }),
        };
      } else if (updateCreatecartItemData?.cartItems) {
        // Explicit cart items provided (per-item add/remove) — use them directly
        updateCartRequest = {
          ...(isSubscriptionFlow && { recommendationItemTerm: selectedPaymentType === CONTRACT ? MONTHLY : NONE }),
          ...(updateCreatecartItemData.cartItems?.length > 0 ? { cartItem: updateCreatecartItemData.cartItems } : {})
        };
      } else {
        // No cart items — only send recommendationItemTerm (e.g. redirect-only call)
        updateCartRequest = {
          ...(isSubscriptionFlow && { recommendationItemTerm: selectedPaymentType === CONTRACT ? MONTHLY : NONE }),
        };
      }

      dispatch({ type: actionTypes.updateAccessoryCartStart, payload: null });
      // Determine usecase: use previousTab only for tab-switch, otherwise use current selectedTab
      const tabForUsecase = updateCreatecartItemData?.previousTab || selectedTab;
      const usecase = (tabForUsecase === TABLETS || tabForUsecase === WATCHES) ? tabForUsecase : undefined;
      // Call the service to make the API request with the prepared requestBody
      const response = updateCartRequest && await multiItemAccessoryUpdateCartService?.getUpdateCartData(
        updateCreatecartItemData?.id,
        updateCartRequest,
        usecase
      );
      if (response) {
        dispatch({ type: actionTypes.updateAccessoryCartSuccess, payload: response });
        dispatch({ type: actionTypes.getCartSuccess, payload: mergeCartData(response?.cartItem) });

        if (!updateCreatecartItemData?.skipRedirect) {
          window.location.href = updateCreatecartItemData?.redirectUrl;
        }
      }
      return response; // Return the response from the API
    } catch (error) {
      dispatch({
        type: actionTypes.updateAccessoryCartFailure,
        payload: { Error: error instanceof Error ? error.message : String(error) }
      });
    }
  }, [dispatch]);

  /**
   * Function to fetch cart data and store in state
   * @param cartId - The cart ID to fetch
   */
  const fetchCartData = useCallback(async (cartId: any) => {
    try {
      dispatch({ type: actionTypes.getCartStart, payload: null });
      
      // Call the service to fetch cart data
      const response = await multiItemAccessoryUpdateCartService?.getCart(cartId);
      
      if (response && !response.error) {
        dispatch({
          type: actionTypes.getCartSuccess,
          payload: findAllCartItemsByCategory(response?.cartItem)
        });
        return response;
      } else {
        throw new Error(response?.error || 'Failed to fetch cart data');
      }
    } catch (error) {
      dispatch({
        type: actionTypes.getCartFailure,
        payload: { Error: error instanceof Error ? error.message : String(error) }
      });
      throw error;
    }
  }, [dispatch]);

  /**
   * Remove a single item from cart via direct API call.
   * Builds the payload from the passed item directly — does not rely on store state.
   */
  const removeItemFromCart = useCallback(async (cartId: any, item: any) => {
    try {
      const tabKey = item?.tabKey;
      const multiAccessoryState = storeRef.current;
      const { selectedPaymentType, recommendedAccessories, deviceSessionValues } = multiAccessoryState;
      const isSubscriptionFlow = [MOBILERATEPLAN, INTERNETFLOW, TV]?.includes?.(
        recommendedAccessories?.[tabKey]?.hardware?.[0]?.recommendationProcessingType
      ) ? true : (deviceSessionValues?.hardwareVariant ? false : true);

      // Build the remove item
      const items: any = [];
      if (tabKey === WATCHES || tabKey === TABLETS) {
        items.push({
          itemType: HARDWARE,
          action: REMOVE,
          id: item?.recomApiResponse?.id,
        });
      } else if (tabKey === ACCESSORY) {
        items.push({
          action: REMOVE,
          id: item?.recomApiResponse?.id,
        });
      }

      if (items.length === 0) return;
      const promotionId = recommendedAccessories?.[tabKey]?.ratePlan?.promotionInfo?.id ?? 'defaultMBBExtraSimBundle'; // Todo with cart response
      const updateCartRequest = {
        ...(isSubscriptionFlow && { recommendationItemTerm: selectedPaymentType === CONTRACT ? MONTHLY : NONE }),
        cartItem: [{
          itemType: tabKey === ACCESSORY ? ACCESSORY : DATA_POSTPAID,
          promotionId: promotionId,
          items: items,
        }],
      };

      dispatch({ type: actionTypes.updateAccessoryCartStart, payload: null });

      const usecase = (tabKey === TABLETS || tabKey === WATCHES) ? tabKey : undefined;
      const response = await multiItemAccessoryUpdateCartService?.getUpdateCartData(
        cartId,
        updateCartRequest,
        usecase
      );

      if (response) {
        dispatch({ type: actionTypes.updateAccessoryCartSuccess, payload: response });
        dispatch({ type: actionTypes.getCartSuccess, payload: mergeCartData(response?.cartItem) });
      }
      return response;
    } catch (error) {
      dispatch({
        type: actionTypes.updateAccessoryCartFailure,
        payload: { Error: error instanceof Error ? error.message : String(error) },
      });
    }
  }, [dispatch]);

  // Return all action creators
  return {
    setupdateAccessoryCartFailure,
    multiItemAccessorycreateCartRequest,
    fetchCartData,
    removeItemFromCart,
  };
};

/**
 * Builds a single-item cart payload for immediate per-item add/remove API calls.
 * Reuses the same note/discount/RATEPLAN logic as accesoryUpdateCartItemList.
 * @param item - The accessory item (with recomApiResponse, imageData, etc.)
 * @param action - 'ADD' or 'REMOVE'
 * @param tabKey - The tab key (ACCESSORY, WATCHES, TABLETS)
 * @param storeState - Current store state snapshot
 * @returns cartItem array ready for the API request body
 */
export const buildSingleItemCartPayload = (item: any, action: string, tabKey: string, storeState: any): any[] => {
  const { deviceSessionValues, selectedPaymentType, recommendedAccessories, cartData } = storeState;
  const isDeviceRenewal = cartData?.processType === 'DEVICE_RENEWAL' || cartData?.processType === 'DEVICE_RP_RENEWAL';
  const isAlreadyInCart = Object.keys(cartData?.[tabKey] || {})?.length > 0;
  const items: any = [];

  if (action === ADD) {
    if (tabKey === WATCHES || tabKey === TABLETS) {
      const cartAction = isAlreadyInCart ? UPDATE : ADD;
      const cartItemNote: any = [
        { author: FLOWINTITIATED, text: HARDWARE },
        { author: HARDWARE_CONFIGURATION, text: item?.recomApiResponse?.variantCategory?.color || '' },
        { author: HARDWARE_IMAGE_URL, text: item?.imageData?.image || '' },
        { author: HARDWAREGROUPNAME, text: item?.recomApiResponse?.baseProducts || item?.recomApiResponse?.name },
        { author: HARDWARE_CATEGORY, text: tabKey === WATCHES ? SMARTWATCH : TABLET_CATEGORY },
        { author: 'isWorldPassBundle', text: 'true' },
        { author: 'enableHardwareBundling', text: 'true' },
      ];
      if (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) {
        cartItemNote.push({ author: UPFRONT, text: '1.0' });
      }
      const ratePlanId = recommendedAccessories?.[tabKey]?.ratePlan?.id;
      if (!isAlreadyInCart && ratePlanId) {
        const ratePlanItem: any = { itemType: RATEPLAN, action: ADD, id: ratePlanId };
        if (isDeviceRenewal && cartData?.subscribedInfo) {
          ratePlanItem.subscribedInfo = {
            id: cartData.subscribedInfo?.id,
            installedPlan: cartData.subscribedInfo?.installedPlan,
          };
        }
        items.push(ratePlanItem);
      }
      items.push({
        itemType: HARDWARE,
        action: cartAction,
        id: item?.recomApiResponse?.id,
        contractTerm: (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) ? 24 : 0,
        note: cartItemNote
      });
    } else {
      // ACCESSORY flow
      const discounts: any = {
        monthlyFlashDiscountPrice: item?.recomApiResponse?.recurringPrice?.marketMonthlyInstallment
          ? countZeros((parseFloat(item?.recomApiResponse?.recurringPrice?.marketMonthlyInstallment) - parseFloat(item?.recomApiResponse?.recurringPrice?.monthlyInstallment)))
          : countZeros(0),
        totalFlashDiscountPrice: item?.recomApiResponse?.oneTimePrice?.market
          ? countZeros(parseFloat(item?.recomApiResponse?.oneTimePrice?.market) - parseFloat(item?.recomApiResponse?.oneTimePrice?.base))
          : countZeros(0)
      };
      const cartItemNote: any = [
        { author: FLOWINTITIATED, text: HARDWARE },
        { author: HARDWARE_IMAGE_URL, text: item?.imageData?.image || '' },
        { author: MONTHLYFLASHDISCOUNT, text: discounts?.monthlyFlashDiscountPrice },
        { author: TOTALFLASHDISCOUNT, text: discounts?.totalFlashDiscountPrice },
        { author: HARDWAREGROUPNAME, text: item?.recomApiResponse?.baseProducts }
      ];
      if (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) {
        cartItemNote.push({ author: UPFRONT, text: 1.0 });
      }
      items.push({
        action: ADD,
        id: item?.recomApiResponse?.id,
        contractTerm: (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) ? 24 : 0,
        note: cartItemNote
      });
    }
  } else if (action === REMOVE) {
    if (tabKey === WATCHES || tabKey === TABLETS) {
      items.push({ itemType: HARDWARE, action: REMOVE, id: item?.recomApiResponse?.id });
    } else if (tabKey === ACCESSORY) {
      items.push({ action: REMOVE, id: item?.recomApiResponse?.id });
    }
  }

  if (items.length === 0) return [];

  const promotionId = recommendedAccessories?.[tabKey]?.ratePlan?.promotionInfo?.id;
  return [{
    itemType: tabKey === ACCESSORY ? ACCESSORY : DATA_POSTPAID,
    promotionId: promotionId,
    items: items
  }];
};
